import {
  compareSignalTasks,
  todayIsoDate,
  type SignalCompleteInput,
  type SignalGateway,
  type SignalListInput,
  type SignalTask,
} from './types';

/**
 * Offline FORQ Signal source. Mirrors the shape a Supabase adapter would return from
 * `public.tasks`, but seeded so the task center is meaningful in demo mode. Tasks are
 * tenant-scoped and carry the domain permission required to act on them, so the center is
 * genuinely permission-aware (revoke `orders.send_to_billing` → the "abrechnen" task vanishes).
 *
 * FTW tasks deep-link to real demo records (Vorgang 202600131/132, Maschine 10001, Kunde 10001)
 * so "Zur Quelle" lands on a populated workbench.
 */
const seedTasks: readonly SignalTask[] = [
  {
    id: 'sig-ftw-billing',
    tenantId: 'ftw',
    category: 'billing',
    title: 'Ersatzteilauftrag 202600131 abrechnen',
    detail: 'Hydraulik-Ersatzteile geliefert – bereit zur Finance-Übergabe.',
    priority: 'high',
    dueAt: '2026-07-15',
    sourceModule: 'Vorgänge',
    route: '/vorgaenge/202600131',
    requiredPermission: 'orders.send_to_billing',
  },
  {
    id: 'sig-ftw-spare',
    tenantId: 'ftw',
    category: 'spare_part',
    title: 'Hydraulikpumpe fehlt – Vorgang 202600132',
    detail: 'Störung Toyota 8FBE16T wartet auf das Ersatzteil.',
    priority: 'high',
    dueAt: '2026-07-15',
    sourceModule: 'Werkstatt',
    route: '/vorgaenge/202600132',
    requiredPermission: 'orders.view',
  },
  {
    id: 'sig-ftw-approval',
    tenantId: 'ftw',
    category: 'approval',
    title: 'Angebot Elektrostapler freigeben',
    detail: 'Verkaufsangebot über der Freigabegrenze – kaufmännische Freigabe nötig.',
    priority: 'urgent',
    dueAt: '2026-07-13',
    sourceModule: 'Verkauf',
    route: '/verkauf/angebote',
    requiredPermission: 'sales.decisions.manage',
  },
  {
    id: 'sig-ftw-inspection',
    tenantId: 'ftw',
    category: 'inspection',
    title: 'UVV-Prüfung Toyota 8FBE16T fällig',
    detail: 'Jährliche Sicherheitsprüfung in wenigen Tagen fällig.',
    priority: 'normal',
    dueAt: '2026-07-20',
    sourceModule: 'Maschinen',
    route: '/maschinen/10001',
    requiredPermission: 'machines.view',
  },
  {
    id: 'sig-ftw-followup',
    tenantId: 'ftw',
    category: 'follow_up',
    title: 'Angebot nachfassen – Werl Industriebedarf',
    detail: 'Angebot seit sieben Tagen offen, noch kein Kundenfeedback.',
    priority: 'normal',
    dueAt: '2026-07-17',
    sourceModule: 'Vertrieb',
    route: '/kunden/10001',
    requiredPermission: 'sales.offers.view',
  },
  {
    id: 'sig-ftw-review',
    tenantId: 'ftw',
    category: 'review',
    title: 'Eingangsrechnung prüfen',
    detail: 'Lieferantenrechnung Hydraulik – Kontierung und Freigabe offen.',
    priority: 'normal',
    dueAt: '2026-07-16',
    sourceModule: 'Finance',
    route: '/finance',
    requiredPermission: 'finance.view',
  },
  {
    id: 'sig-stw-return',
    tenantId: 'stw',
    category: 'return',
    title: 'Mietrückgabe erfassen – Transportgerät',
    detail: 'Rückgabe fällig, Zustand und Betriebsstunden aufnehmen.',
    priority: 'high',
    dueAt: '2026-07-15',
    sourceModule: 'Maschinen',
    route: '/maschinen',
    requiredPermission: 'rental.view',
  },
  {
    id: 'sig-stw-billing',
    tenantId: 'stw',
    category: 'billing',
    title: 'Transportauftrag abrechnen',
    detail: 'Abgeschlossener Vorgang wartet auf die Abrechnung.',
    priority: 'normal',
    dueAt: '2026-07-18',
    sourceModule: 'Vorgänge',
    route: '/vorgaenge',
    requiredPermission: 'orders.send_to_billing',
  },
  {
    id: 'sig-gsl-inspection',
    tenantId: 'gsl',
    category: 'inspection',
    title: 'UVV fällig – Sauerland Flotte',
    detail: 'Zwei Stapler mit fälliger Sicherheitsprüfung.',
    priority: 'high',
    dueAt: '2026-07-14',
    sourceModule: 'Maschinen',
    route: '/maschinen',
    requiredPermission: 'machines.view',
  },
  {
    id: 'sig-gsl-approval',
    tenantId: 'gsl',
    category: 'approval',
    title: 'Rechnung freigeben',
    detail: 'Ausgangsrechnung wartet auf kaufmännische Freigabe.',
    priority: 'normal',
    dueAt: '2026-07-16',
    sourceModule: 'Finance',
    route: '/finance',
    requiredPermission: 'finance.view',
  },
];

export class DemoSignalGateway implements SignalGateway {
  private readonly completed = new Set<string>();

  async listOpenTasks({ tenantId, permissions }: SignalListInput): Promise<readonly SignalTask[]> {
    const today = todayIsoDate();
    return seedTasks
      .filter((task) => task.tenantId === tenantId)
      .filter((task) => !this.completed.has(task.id))
      .filter((task) => permissions.has(task.requiredPermission))
      .slice()
      .sort((left, right) => compareSignalTasks(left, right, today));
  }

  async completeTask({ tenantId, taskId, permissions }: SignalCompleteInput): Promise<readonly SignalTask[]> {
    this.completed.add(taskId);
    return this.listOpenTasks({ tenantId, permissions });
  }
}

export function createDemoSignalGateway(): SignalGateway {
  return new DemoSignalGateway();
}
